import React, { useState, useRef } from 'react'
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverBody, Tooltip } from '@chakra-ui/core'
import LocaleText from 'components/LocaleText'
import { GroupedSelectHeader } from './GroupedSelectHeader'
import { GroupedSelectList } from './GroupedSelectList'
import { GroupedSelectFooter } from './GroupedSelectFooter'
import {
  getAllItemIds,
  filterDataBySearch,
  getSelectedByGroup,
  hasAllSelected,
  getInitialSelectedIds,
  handleItemToggle,
} from './utils'
import { ItemGroup, GroupedSelectMode, SelectedIdsRecord } from './types'
import { NoResults } from '../GroupsTree/NoResults'
import { isEmpty } from 'lodash'
import { NoFocusButton } from '../NoFocusButton'
import { isRtlSelector } from 'redux/selectors/general'
import { useSelector } from 'react-redux'

interface GroupedSelectProps {
  data: ItemGroup[]
  selectedIds: SelectedIdsRecord
  onSubmit: (selectedByGroup: SelectedIdsRecord) => void
  placeholder?: string
  mode?: GroupedSelectMode
  isShowSelectAllButton?: boolean
  allToggleItemId?: string // ID element for switching "select all/clear selection from all"
  customTrigger?: React.ReactElement
}

const TRIGGER_BUTTON_STYLES = {
  p: '4px' as const,
  borderRadius: '5px' as const,
  bg: 'white' as const,
}

export const GroupedSelect: React.FC<GroupedSelectProps> = ({
  data,
  selectedIds,
  onSubmit,
  placeholder = 'Select options',
  mode = 'multiple',
  isShowSelectAllButton = true,
  allToggleItemId,
  customTrigger,
}) => {
  const isRtl = useSelector(isRtlSelector)
  const [searchText, setSearchText] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  // Ref for search input
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Get all element IDs from data
  const allItemIds = getAllItemIds(data)

  // Get initial selected IDs
  const initialSelectedIds = getInitialSelectedIds({ selectedIds })

  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>(initialSelectedIds)

  const filteredData = filterDataBySearch(data, searchText)

  const handleSelectAll = () => setInternalSelectedIds(allItemIds)
  const handleClearAll = () => setInternalSelectedIds([])

  const handleToggleItem = (itemId: string) => {
    if (mode === 'single') {
      setInternalSelectedIds((prev) => (prev.includes(itemId) ? [] : [itemId]))
    } else {
      setInternalSelectedIds((prev) => handleItemToggle({ itemId, currentSelectedIds: prev, allToggleItemId }))
    }
  }
  const handleSubmit = () => {
    const selectedByGroup = getSelectedByGroup({ data, selectedIds: internalSelectedIds })
    onSubmit(selectedByGroup)
    setIsOpen(false)
  }
  const handleOpen = () => {
    const newSelectedIds = getInitialSelectedIds({ selectedIds })
    setInternalSelectedIds(newSelectedIds)
    setIsOpen(true)
  }
  const handleClose = () => setIsOpen(false)

  // Get data for trigger
  const allItems = data.flatMap((g) => g.items)
  const hasAll = allToggleItemId ? hasAllSelected(selectedIds, allToggleItemId) : false
  const selectedIdsArray = hasAll ? allItemIds : Object.values(selectedIds).flat()
  const selectedItemsForTrigger = allItems.filter((item) => selectedIdsArray.includes(item.id))

  // Generate trigger button text
  const getTriggerButtonText = (): React.ReactNode => {
    if (selectedItemsForTrigger.length === 0) {
      return <LocaleText text={placeholder} />
    }
    if (selectedItemsForTrigger.length === 1) {
      return selectedItemsForTrigger[0].name
    }
    return `${selectedItemsForTrigger[0].name} +${selectedItemsForTrigger.length - 1}`
  }

  // Generate tooltip text
  const tooltipText =
    selectedItemsForTrigger.length > 0 ? selectedItemsForTrigger.map((item) => item.name).join(', ') : ''

  const isNoResults = !filteredData.length
  const isDisabled = isEmpty(internalSelectedIds)
  const hasSelectedItems = selectedItemsForTrigger.length > 0

  // Render trigger button
  const renderTrigger = () => {
    if (customTrigger) {
      return customTrigger
    }

    return (
      <span>
        {hasSelectedItems ? (
          <Tooltip
            label={tooltipText}
            aria-label={tooltipText}
            placement="top"
            hasArrow
            shouldWrapChildren
            maxW="250px"
            whiteSpace="pre-line"
          >
            <NoFocusButton {...TRIGGER_BUTTON_STYLES}>{getTriggerButtonText()}</NoFocusButton>
          </Tooltip>
        ) : (
          <NoFocusButton {...TRIGGER_BUTTON_STYLES}>{getTriggerButtonText()}</NoFocusButton>
        )}
      </span>
    )
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      placement={isRtl ? 'bottom-start' : 'bottom-end'}
      initialFocusRef={searchInputRef}
    >
      <PopoverTrigger>{renderTrigger()}</PopoverTrigger>
      <PopoverContent zIndex={99999} w="100%" maxW="350px" _focus={{ boxShadow: 'none' }}>
        <PopoverHeader>
          <GroupedSelectHeader
            searchText={searchText}
            setSearchText={setSearchText}
            onSelectAll={isShowSelectAllButton ? handleSelectAll : undefined}
            onClear={handleClearAll}
            mode={mode}
            isNoResults={isNoResults}
            hasSelectedItems={internalSelectedIds.length > 0}
            searchInputRef={searchInputRef}
          />
        </PopoverHeader>
        <PopoverBody overflowY="auto" maxH="300px">
          {!filteredData.length ? (
            <NoResults />
          ) : (
            <GroupedSelectList data={filteredData} selectedIds={internalSelectedIds} onToggle={handleToggleItem} />
          )}
        </PopoverBody>
        <GroupedSelectFooter onSubmit={handleSubmit} isDisabled={isDisabled} />
      </PopoverContent>
    </Popover>
  )
}
